import dynamic from 'next/dynamic';

import type { InspirationPostsSectionProps } from 'src/components/sections/InspirationPostsSection/InspirationPostsSection';
import { getInspirationPostsForTerm } from 'src/content/inspiration/getInspirationPostsForTerm';
import { toInspirationCardProps } from 'src/content/inspiration/toInspirationCardProps';
import { CATEGORY_PREVIEW_SIZE } from 'src/content/inspiration/toInspirationHubProps';
import { fetchInspirationLibrary } from 'src/services/contentstack/fetchInspirationLibrary';
import { INSPIRATION_CATEGORY_TAXONOMY_UID } from 'src/services/contentstack/fetchInspirationTermNames';
import type { SectionRenderer, UISectionData } from 'src/types/Section';
import type { TransformerContext } from 'src/types/Transformer';
import { lazyLoadImages } from 'src/utils/images';
import { getInspirationCategoryPath } from 'src/utils/inspirationUrls';
import type { InspirationPostsContentSection } from './types';

export const DEFAULT_INSPIRATION_POSTS_HEADING = 'Inspiration';

/* istanbul ignore next */
export const renderer: SectionRenderer<InspirationPostsSectionProps> =
  dynamic<InspirationPostsSectionProps>(() =>
    import('src/components/sections/InspirationPostsSection/InspirationPostsSection').then(
      (mod) => mod.InspirationPostsSection,
    ),
  );

/**
 * The latest posts in the page's blog category. Null, so the block renders
 * nothing, when the page has no category or the category has no posts.
 */
export const transformer = async (
  { _metadata, inspiration_posts_heading }: InspirationPostsContentSection,
  { pageMetadata, pageTerms, positionMetadata }: TransformerContext,
): Promise<UISectionData<InspirationPostsSectionProps> | null> => {
  const term = pageTerms.find(
    ({ taxonomyUid }) => taxonomyUid === INSPIRATION_CATEGORY_TAXONOMY_UID,
  );

  if (!term) {
    return null;
  }

  const library = await fetchInspirationLibrary(pageMetadata.isPreview);
  const posts = getInspirationPostsForTerm(library, term, CATEGORY_PREVIEW_SIZE);

  if (posts.length === 0) {
    return null;
  }

  return {
    uid: _metadata.uid,
    props: {
      heading: inspiration_posts_heading?.trim() || DEFAULT_INSPIRATION_POSTS_HEADING,
      href: getInspirationCategoryPath(term.termUid),
      posts: toInspirationCardProps(posts, {
        eagerImageCount: lazyLoadImages(positionMetadata.position) ? 0 : 1,
      }),
    },
  };
};
